import React, { useEffect, useState } from "react";
import DataGrid from "./DataGrid/DataGrid.tsx";
import { BankReportMappingEnum } from "../../enum/normalization-mapping.enum.ts";
import DatePicker from "../Forms/DatePicker/DatePicker.tsx";

interface PoliciesReportDataGridProps {
  policies: any[];
  isLoading: boolean;
  selectedPolicies: [string, string, string][];
  setSelectedPolicies: React.Dispatch<
    React.SetStateAction<[string, string, string][]>
  >;
  setPolicies: React.Dispatch<React.SetStateAction<any[]>>;

  printReportMetadata?: Array<{ label: string; value: string }>;
  printReportFooterMetadata?: Array<{ label: string; value: string }>;

  tableName: string;
  fileName: string;
}

const BankReportDataGrid: React.FC<PoliciesReportDataGridProps> = ({
  policies,
  selectedPolicies,
  setSelectedPolicies,
  setPolicies,
  isLoading,
  printReportMetadata,
  printReportFooterMetadata,
  fileName,
  tableName,
}) => {
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  useEffect(() => {
    if (policies.length > 0) {
      const allSelected = policies.every((policy) =>
        selectedPolicies.some(
          ([polId, payNum]) =>
            polId === policy[BankReportMappingEnum.POL_ID] &&
            payNum === policy[BankReportMappingEnum.PAY_NUM]
        )
      );
      setSelectAllChecked(allSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [policies, selectedPolicies]);

  const handleSelectAll = (isChecked: boolean) => {
    setSelectAllChecked(isChecked);
    setSelectedPolicies(
      isChecked
        ? policies.map((policy) => [
            policy[BankReportMappingEnum.POL_ID],
            policy[BankReportMappingEnum.PAY_NUM],
            policy[BankReportMappingEnum.DATE_PAYMENT] || "",
          ])
        : []
    );
  };

  const handleRowSelection = (policy: any, isChecked: boolean) => {
    setSelectedPolicies((prev) => {
      const updated = [...prev];
      const entry: [string, string, string] = [
        policy[BankReportMappingEnum.POL_ID],
        policy[BankReportMappingEnum.PAY_NUM],
        policy[BankReportMappingEnum.DATE_PAYMENT] || "",
      ];
      const index = updated.findIndex(
        ([polId, payNum]) => polId === entry[0] && payNum === entry[1]
      );

      if (isChecked && index === -1) {
        updated.push(entry);
      } else if (!isChecked && index !== -1) {
        updated.splice(index, 1);
      }

      return updated;
    });
  };

  const handleDateChange = (
    policyId: string,
    payNum: string,
    newDate: string
  ) => {
    setPolicies((prevPolicies) =>
      prevPolicies.map((policy) =>
        policy[BankReportMappingEnum.POL_ID] === policyId &&
        policy[BankReportMappingEnum.PAY_NUM] === payNum
          ? { ...policy, [BankReportMappingEnum.DATE_PAYMENT]: newDate }
          : policy
      )
    );

    setSelectedPolicies((prev) =>
      prev.map(([id, num, date]) =>
        id === policyId && num === payNum ? [id, num, newDate] : [id, num, date]
      )
    );
  };

  const getPrintRows = (filteredRows: any[]) => {
    if (selectedPolicies.length === 0) return filteredRows;
    return filteredRows.filter((row) =>
      selectedPolicies.some(
        ([polId, payNum]) =>
          polId === row[BankReportMappingEnum.POL_ID] &&
          payNum === row[BankReportMappingEnum.PAY_NUM]
      )
    );
  };

  const columns = [
    {
      name: "selected",
      header: (
        <div className="flex h-full items-center justify-center">
          <input
            type="checkbox"
            checked={selectAllChecked}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </div>
      ),
      render: ({ data }) => (
        <div className="flex h-full items-center justify-center">
          <input
            type="checkbox"
            checked={selectedPolicies.some(
              ([polId, payNum]) =>
                polId === data[BankReportMappingEnum.POL_ID] &&
                payNum === data[BankReportMappingEnum.PAY_NUM]
            )}
            onChange={(e) => handleRowSelection(data, e.target.checked)}
          />
        </div>
      ),
      minWidth: 50,
      maxWidth: 50,
      type: "checkbox",
      sortable: false,
      filterable: false,
      enableColumnFilterContextMenu: false,
    },
    {
      name: BankReportMappingEnum.DATE_PAYMENT,
      render: ({ data }) => {
        return (
          <DatePicker
            name="bankReportDate"
            classNameAdditional="border-none pl-0"
            value={data[BankReportMappingEnum.DATE_PAYMENT] || ""}
            onChange={(event) =>
              handleDateChange(
                data[BankReportMappingEnum.POL_ID],
                data[BankReportMappingEnum.PAY_NUM],
                event.target.value
              )
            }
          />
        );
      },
      header: "Дата на превод",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.NAME,
      header: "Име",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.POL_NUM,
      header: "№ Полица",
      minWidth: 140,
      type: "string",
    },
    {
      name: BankReportMappingEnum.INS_COMP,
      header: "ЗК",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.AGENT,
      header: "Агент",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.POL_KIND,
      header: "Вид Полица",
      minWidth: 150,
      type: "string",
    },
    {
      name: BankReportMappingEnum.SUMA,
      header: "Сума",
      minWidth: 120,
      type: "string",
    },
    {
      name: BankReportMappingEnum.DATE_ND,
      header: "Дата на Вноска",
      minWidth: 120,
      type: "date",
    },
    {
      name: BankReportMappingEnum.REG_NO,
      header: "ДКН",
      minWidth: 120,
      type: "date",
    },
    {
      name: BankReportMappingEnum.PAY_NUM,
      header: "№ Падеж",
      minWidth: 120,
      type: "date",
    },
  ];

  return (
    <DataGrid
      columns={columns}
      dataSource={policies}
      isLoading={isLoading}
      printReportMetadata={printReportMetadata}
      printReportFooterMetadata={printReportFooterMetadata}
      getPrintRows={getPrintRows}
      gridTitle={tableName}
      idProperty={BankReportMappingEnum.POL_ID}
      csvFilename={fileName}
      icon="fa-kit fa-regular-file-circle-question"
    />
  );
};

export default BankReportDataGrid;
